import React, { useState } from 'react'
import { motion } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { StripeService } from '../../services/stripeService'

const { FiDatabase, FiArrowLeft, FiCheck, FiLoader } = FiIcons

const UnlockModal = ({ show, onBack, error, user }) => {
  if (!show) return null

  const [subscribingPlan, setSubscribingPlan] = useState('')
  const [localError, setLocalError] = useState('')

  const features = [
    'Unlimited access to 1,000s of curated link opportunities',
    '100% Include direct contact information',
    'Custom link requests from our expert team + VIP Support',
    'Filter by keyword, niche, link type, and location',
    'AI-powered personalized content generation',
    'Status tracking for your outreach',
    'Export and email sending capabilities',
    'Get over 20% free bonus credits each month',
    'Credits roll over every month, and you can cancel anytime'
  ]

  const SUBSCRIPTION_PLANS = [
    { id: 'price_1RxUwGI8bx27Wh5OX4QQ1TMR', name: 'Pro Plan', price: 29, credits: 35, savings: '21% savings', description: '35 credits + Unlimited Pro Links' },
    { id: 'price_1RxUyII8bx27Wh5OVnaoGdLq', name: 'Agency Plan', price: 79, credits: 100, savings: '27% savings', description: '100 credits + Unlimited Pro Links' }
  ]

  const handleSubscribe = async (planId) => {
    try {
      setLocalError('')
      setSubscribingPlan(planId)

      const origin = window.location.origin
      const successUrl = `${origin}/#/dashboard?payment=success`
      const cancelUrl = `${origin}/#/dashboard?payment=cancelled`
      const session = await StripeService.createSubscriptionCheckoutSession(
        user.id,
        planId,
        successUrl,
        cancelUrl,
        true
      )

      if (session?.url) {
        window.location.href = session.url
      } else {
        throw new Error('Failed to create subscription checkout session')
      }
    } catch (e) {
      console.error('Subscription error:', e)
      setLocalError('Failed to initialize subscription checkout. Please try again.')
      setSubscribingPlan('')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="bg-[#D59A4C30] p-4 rounded-full w-16 h-16 mx-auto mb-4">
              <SafeIcon icon={FiDatabase} className="w-8 h-8 text-[#D59A4C]" />
            </div>
            <h2 className="text-3xl text-gray-900 mb-2 font-bold">
              Unlock Pro Links Directory
            </h2>
            <p style={{
      color: 'rgb(213, 154, 76)',
      fontWeight: '700'
    }} className="text-[#D59A4C] text-xl font-bold">Unlimited access to our database of backlink opportunities</p>
          </div>

          {/* Features */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              What you'll get:
            </h3>
            <div className="space-y-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <div className="bg-green-100 p-1 rounded-full mr-3 mt-0.5">
                    <SafeIcon icon={FiCheck} className="w-3 h-3 text-green-600" />
                  </div>
                  <span style={{
      color: 'rgb(55, 65, 81)',
      fontWeight: '500',
      fontSize: '18px'
    }} className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Error Message */}
          {(localError || error) && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
              <p className="text-red-700">{localError || error}</p>
            </div>
          )}

          {/* Subscription Plans */}
          <div className="mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {SUBSCRIPTION_PLANS.map((plan) => (
                <div key={plan.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow">
                  <span>{plan.name}</span> &nbsp; <span class="px-2 py-1 bg-green-100 text-green-700 rounded-40 text-xs font-medium dark:bg-green-900/30 dark:text-green-400">{plan.savings}</span>
                  <div className="text-3xl font-extrabold text-gray-900 mb-1">${plan.price}/mo</div>
                  <div className="text-sm text-gray-500 mb-4"><strong>{plan.description}</strong></div>
                  <button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribingPlan === plan.id}
                    className="w-full flex items-center justify-center px-6 py-3 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {subscribingPlan === plan.id ? (
                      <>
                        <SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
                        Redirecting...
                      </>
                    ) : 'Subscribe'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Info */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Secure payment processing • Credits never expire • No refunds</p>
          </div>

          {/* Back Button */}
          <div className="flex gap-4 mt-6">
            <button
              onClick={onBack}
              className="flex items-center px-6 py-2 bg-transparent text-gray-700 rounded-lg hover:bg-gray-200 transition-colors no-border"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
              Back
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default UnlockModal
